/*

& "Attach listings (Maximum 5)" in a chat (3 Nov 2026, gap A8), the app's sheet: your published listings, newest first,
& with a search by property name; pick up to five and "Done" sends them on their own (chat/send-message's shared_entity).
& The list is chat/fetch-shareable-listings, for the party you act as.

*/

import { Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React from "react";

import BeegruButton from "../../../common-components/buttons/BeegruButton";

const MAX_LISTINGS: number = 5;

type Listing = { _id: string; title: string; location_title: string };

const ChatListingPickerDialog = ({
	open,
	onClose,
	profile_context,
	onDone,
}: {
	open: boolean;
	onClose: () => void;
	profile_context: any;
	/* The picked listing ids, in the order picked. */
	onDone: (propertyIds: Array<string>) => void;
}) => {
	const router = useRouter();
	/* Arabic and Kannada beside English, as the screens around this one write them (router.locale). */
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;

	const [query, setQuery] = React.useState<string>("");
	const [listings, setListings] = React.useState<Array<Listing> | null>(null);
	const [picked, setPicked] = React.useState<Array<string>>([]);
	/* A failed read says so, rather than looking like "no published listings". */
	const [failed, setFailed] = React.useState<boolean>(false);
	const [attempt, setAttempt] = React.useState<number>(0);

	React.useEffect(() => {
		if (!open) return;
		setQuery("");
		setPicked([]);
	}, [open]);

	/* The list, read again on a short pause in typing. */
	React.useEffect(() => {
		if (!open) return;
		let active = true;
		setListings(null);
		setFailed(false);
		const timer = setTimeout(() => {
			fetch(process.env.PRODUCTION_API_URL + "chat/fetch-shareable-listings", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "text/plain" },
				body: JSON.stringify({
					is_business_profile: profile_context?.is_business_profile === true,
					business_profile_id: profile_context?.is_business_profile ? profile_context.business_profile_id : undefined,
					search_text: query.trim(),
				}),
			})
				.then((res) => res.json())
				.then((data) => {
					if (!active) return;
					if (data?.status === "success") setListings(data.payload.listings);
					else setFailed(true);
				})
				.catch(() => active && setFailed(true));
		}, 300);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [open, query, attempt, profile_context?.is_business_profile, profile_context?.business_profile_id]);

	const toggle = (id: string) =>
		setPicked((current) =>
			current.includes(id) ? current.filter((x) => x !== id) : current.length >= MAX_LISTINGS ? current : [...current, id],
		);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle>{tr("Attach listings (Maximum 5)", "إرفاق عقارات (5 كحد أقصى)", "ಪಟ್ಟಿಗಳನ್ನು ಲಗತ್ತಿಸಿ (ಗರಿಷ್ಠ 5)")}</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
				<TextField
					size="small"
					fullWidth
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={tr("Search by property name", "ابحث باسم العقار", "ಆಸ್ತಿಯ ಹೆಸರಿನಿಂದ ಹುಡುಕಿ")}
				/>
				{failed ? (
					<div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "1rem 0" }}>
						<Typography sx={{ fontSize: "0.875rem", color: "#757575" }}>
							{tr("We couldn't load your listings.", "تعذّر تحميل عقاراتك.", "ನಿಮ್ಮ ಪಟ್ಟಿಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ.")}
						</Typography>
						<BeegruButton variant="outlined" flavor="primary" size="small" onClick={() => setAttempt((n) => n + 1)}>
							{tr("Try again", "حاول مرة أخرى", "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ")}
						</BeegruButton>
					</div>
				) : listings === null ? (
					<CircularProgress
						size={22}
						sx={{ alignSelf: "center", color: "#B05920", margin: "1rem 0" }}
					/>
				) : listings.length === 0 ? (
					<Typography sx={{ fontSize: "0.875rem", color: "#757575", padding: "1rem 0" }}>
						{query.trim()
							? tr("No listings match your search.", "لا توجد عقارات تطابق بحثك.", "ನಿಮ್ಮ ಹುಡುಕಾಟಕ್ಕೆ ಯಾವುದೇ ಪಟ್ಟಿ ಹೊಂದಿಕೆಯಾಗಲಿಲ್ಲ.")
							: tr("You have no published listings to share.", "ليس لديك عقارات منشورة لمشاركتها.", "ಹಂಚಿಕೊಳ್ಳಲು ನಿಮ್ಮ ಬಳಿ ಪ್ರಕಟಿತ ಪಟ್ಟಿಗಳಿಲ್ಲ.")}
					</Typography>
				) : (
					listings.map((listing) => (
						<label
							key={listing._id}
							style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer" }}
						>
							<Checkbox
								size="small"
								checked={picked.includes(listing._id)}
								disabled={!picked.includes(listing._id) && picked.length >= MAX_LISTINGS}
								onChange={() => toggle(listing._id)}
							/>
							<div style={{ minWidth: 0 }}>
								<Typography
									variant="body2"
									noWrap
								>
									{listing.title}
								</Typography>
								{listing.location_title ? (
									<Typography
										variant="caption"
										color="text.secondary"
										noWrap
									>
										{listing.location_title}
									</Typography>
								) : null}
							</div>
						</label>
					))
				)}
			</DialogContent>
			<DialogActions>
				<BeegruButton
					variant="outlined"
					flavor="primary"
					size="small"
					onClick={onClose}
				>
					{tr("Cancel", "إلغاء", "ರದ್ದುಮಾಡಿ")}
				</BeegruButton>
				<BeegruButton
					variant="contained"
					flavor="primary"
					size="small"
					disabled={picked.length === 0}
					onClick={() => onDone(picked)}
				>
					{tr("Done", "تم", "ಮುಗಿದಿದೆ")}
				</BeegruButton>
			</DialogActions>
		</Dialog>
	);
};

export default ChatListingPickerDialog;
